/**
 * FilterTreeBuilder — the VISUAL boolean-filter builder (ADR-0055 S4). The graphical
 * twin of `ctx.db.query('deals', { filters: … })`: a recursive tree of LEAVES
 * (`{column, op, value}`) and GROUPS (`E`/`O` = and/or, optionally negated `NON`),
 * with "+ Condizione" / "+ Gruppo" so a non-coder builds the same `_build_where`
 * tree the code form produces.
 *
 * It holds the ergonomic ROOT group in internal state (so a half-typed leaf survives
 * keystrokes) and emits the NORMALISED wire `spec.filters` (a list) via `onChange`.
 * Seed once from `value`; the parent gives it a `key` per query so it re-seeds on
 * switch. Pure round-trip + model live in `lib/filterTree`.
 */
import { useState } from "react";
import { Plus, X } from "lucide-react";

import {
  FILTER_TREE_OPS,
  NO_VALUE_OPS,
  ARRAY_OPS,
  type FilterGroup,
  type FilterLeaf,
  type FilterNode,
  type WireNode,
  newGroup,
  newLeaf,
  specToRoot,
  rootToSpec,
} from "@/lib/filterTree";

const SEL = "rounded-md border border-border bg-surface px-2 py-1 text-xs disabled:opacity-50";
const INP = "min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs disabled:opacity-50";
const ICON_BTN = "shrink-0 rounded p-1 text-muted-foreground hover:text-danger disabled:opacity-40";
const ADD_BTN =
  "flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground hover:bg-foreground/5 disabled:opacity-50";

const replaceAt = <T,>(arr: T[], i: number, v: T): T[] => arr.map((x, j) => (j === i ? v : x));
const removeAt = <T,>(arr: T[], i: number): T[] => arr.filter((_, j) => j !== i);

function LeafRow({
  leaf,
  columns,
  disabled,
  onChange,
  onRemove,
}: {
  leaf: FilterLeaf;
  columns: string[];
  disabled?: boolean;
  onChange: (leaf: FilterLeaf) => void;
  onRemove: () => void;
}) {
  const noValue = NO_VALUE_OPS.has(leaf.op);
  return (
    <div className="flex items-center gap-1.5">
      <select
        className={`${SEL} max-w-[10rem] flex-1`}
        value={leaf.column}
        disabled={disabled}
        onChange={(e) => onChange({ ...leaf, column: e.target.value })}
      >
        <option value="">— colonna —</option>
        {!columns.includes(leaf.column) && leaf.column && <option value={leaf.column}>{leaf.column}</option>}
        {columns.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <select
        className={SEL}
        value={leaf.op}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...leaf, op: e.target.value, ...(NO_VALUE_OPS.has(e.target.value) ? { value: undefined } : {}) })
        }
      >
        {FILTER_TREE_OPS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {!noValue && (
        <input
          className={INP}
          value={String(leaf.value ?? "")}
          placeholder={ARRAY_OPS.has(leaf.op) ? "a, b, c" : "valore"}
          disabled={disabled}
          onChange={(e) => onChange({ ...leaf, value: e.target.value })}
        />
      )}
      <button type="button" aria-label="Rimuovi condizione" onClick={onRemove} disabled={disabled} className={ICON_BTN}>
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function GroupBlock({
  group,
  columns,
  disabled,
  isRoot,
  onChange,
  onRemove,
}: {
  group: FilterGroup;
  columns: string[];
  disabled?: boolean;
  isRoot?: boolean;
  onChange: (group: FilterGroup) => void;
  onRemove?: () => void;
}) {
  const setChild = (i: number, node: FilterNode) => onChange({ ...group, children: replaceAt(group.children, i, node) });
  const dropChild = (i: number) => onChange({ ...group, children: removeAt(group.children, i) });
  const addLeaf = () => onChange({ ...group, children: [...group.children, newLeaf(columns[0] ?? "")] });
  const addGroup = () => onChange({ ...group, children: [...group.children, newGroup("and")] });

  return (
    <div className={isRoot ? "space-y-2" : "space-y-2 rounded-md border border-border/70 bg-foreground/[0.02] p-2"}>
      <div className="flex items-center gap-1.5">
        {/* E / O segmented toggle (AND / OR) */}
        <div className="inline-flex overflow-hidden rounded-md border border-border text-[11px]">
          {(["and", "or"] as const).map((c) => (
            <button
              key={c}
              type="button"
              disabled={disabled}
              onClick={() => onChange({ ...group, combinator: c })}
              className={`px-2 py-0.5 ${group.combinator === c ? "bg-foreground text-background" : "text-muted-foreground hover:bg-foreground/5"}`}
            >
              {c === "and" ? "E" : "O"}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <input
            type="checkbox"
            checked={group.negate}
            disabled={disabled}
            onChange={(e) => onChange({ ...group, negate: e.target.checked })}
          />
          NON
        </label>
        <span className="text-[11px] text-muted-foreground/70">
          {group.combinator === "and" ? "tutte le condizioni" : "almeno una condizione"}
        </span>
        {!isRoot && onRemove && (
          <button type="button" aria-label="Rimuovi gruppo" onClick={onRemove} disabled={disabled} className={`${ICON_BTN} ml-auto`}>
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {group.children.length === 0 && (
        <p className="pl-1 text-[11px] italic text-muted-foreground/70">Nessuna condizione — tutte le righe.</p>
      )}
      <div className="space-y-2 border-l border-border/50 pl-2">
        {group.children.map((child, i) =>
          child.type === "leaf" ? (
            <LeafRow
              key={i}
              leaf={child}
              columns={columns}
              disabled={disabled}
              onChange={(l) => setChild(i, l)}
              onRemove={() => dropChild(i)}
            />
          ) : (
            <GroupBlock
              key={i}
              group={child}
              columns={columns}
              disabled={disabled}
              onChange={(g) => setChild(i, g)}
              onRemove={() => dropChild(i)}
            />
          ),
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <button type="button" onClick={addLeaf} disabled={disabled} className={ADD_BTN}>
          <Plus className="h-3 w-3" /> Condizione
        </button>
        <button type="button" onClick={addGroup} disabled={disabled} className={ADD_BTN}>
          <Plus className="h-3 w-3" /> Gruppo
        </button>
      </div>
    </div>
  );
}

export function FilterTreeBuilder({
  columns,
  value,
  onChange,
  disabled,
}: {
  /** The table's column names (leaf column picker). */
  columns: string[];
  /** The stored `spec.filters` (list / single node / empty). */
  value: unknown;
  /** Emits the normalised wire `spec.filters` (always a list). */
  onChange: (filters: WireNode[]) => void;
  disabled?: boolean;
}) {
  const [root, setRoot] = useState<FilterGroup>(() => specToRoot(value));
  const update = (next: FilterGroup) => {
    setRoot(next);
    onChange(rootToSpec(next));
  };
  return <GroupBlock group={root} columns={columns} disabled={disabled} isRoot onChange={update} />;
}
